"use client";

import { motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/** Shared easing — a soft easeOutExpo that reads as "premium settle". */
export const MARKETING_EASE = [0.22, 1, 0.36, 1] as const;

type Direction = "up" | "down" | "left" | "right" | "none";

type RevealProps = {
  children: ReactNode;
  /** Where the element travels in FROM. */
  direction?: Direction;
  /** Travel distance in pixels. */
  distance?: number;
  delay?: number;
  duration?: number;
  /** Soft de-blur on entry for a "focus pull" feel. */
  blur?: boolean;
  /** Fraction of the element that must be visible before it fires. */
  amount?: number;
  className?: string;
};

const offsetFor = (direction: Direction, distance: number) => {
  switch (direction) {
    case "up":
      return { y: distance };
    case "down":
      return { y: -distance };
    case "left":
      return { x: -distance };
    case "right":
      return { x: distance };
    default:
      return {};
  }
};

/**
 * Scroll-reveal wrapper. Fades + slides + de-blurs its children into place the
 * first time they enter the viewport. Honours `prefers-reduced-motion` by
 * rendering the children statically (no hidden initial state), so the content
 * is never left invisible for motion-sensitive visitors.
 */
export function Reveal({
  children,
  direction = "up",
  distance = 24,
  delay = 0,
  duration = 0.7,
  blur = true,
  amount = 0.35,
  className,
}: RevealProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={cn(className)}
      initial={{
        opacity: 0,
        filter: blur ? "blur(10px)" : "blur(0px)",
        ...offsetFor(direction, distance),
      }}
      whileInView={{ opacity: 1, filter: "blur(0px)", x: 0, y: 0 }}
      viewport={{ once: true, amount }}
      transition={{ duration, delay, ease: MARKETING_EASE }}
    >
      {children}
    </motion.div>
  );
}
